/**
 * Spotlight coachmark overlay for the product tour.
 *
 * Desktop: expand the left rail (do NOT open the mobile drawer — when
 * mobileOpen is true the desktop rail unmounts and only a sm:hidden drawer
 * remains, so targets measure as 0×0 at the origin).
 * Mobile: open the drawer so sidebar targets exist.
 */

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import type { ProductTourChatControls, ProductTourStep } from './types.js';

const PAD = 8;
const CARD_GAP = 12;
/** Tailwind `sm` breakpoint — matches ConversationSidebar mobile/desktop split. */
const MOBILE_MAX_PX = 639;

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

interface ProductTourOverlayProps {
  steps: ProductTourStep[];
  chatControls: ProductTourChatControls | null;
  onSkip: () => void;
  onComplete: () => void;
  saving?: boolean;
  error?: string | null;
}

function isMobileViewport(): boolean {
  return window.matchMedia(`(max-width: ${MOBILE_MAX_PX}px)`).matches;
}

/**
 * Prefer a visible target. Hidden (`display:none` / zero-box) nodes are ignored
 * so a sm:hidden mobile drawer never steals measurement on desktop.
 */
function findTarget(dataTour: string): HTMLElement | null {
  const nodes = document.querySelectorAll<HTMLElement>(
    `[data-tour="${CSS.escape(dataTour)}"]`,
  );
  for (const el of nodes) {
    const r = el.getBoundingClientRect();
    if (r.width >= 1 && r.height >= 1) return el;
  }
  return null;
}

function measure(el: HTMLElement): Rect {
  const r = el.getBoundingClientRect();
  return {
    top: r.top,
    left: r.left,
    width: r.width,
    height: r.height,
  };
}

function waitFrames(n: number): Promise<void> {
  return new Promise((resolve) => {
    let left = n;
    const tick = () => {
      left -= 1;
      if (left <= 0) resolve();
      else requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  });
}

export function ProductTourOverlay({
  steps,
  chatControls,
  onSkip,
  onComplete,
  saving = false,
  error = null,
}: ProductTourOverlayProps) {
  const [index, setIndex] = useState(0);
  const [hole, setHole] = useState<Rect | null>(null);
  const [cardStyle, setCardStyle] = useState<CSSProperties>({});
  const cardRef = useRef<HTMLDivElement>(null);
  const step = steps[index];
  const total = steps.length;
  const isLast = index >= total - 1;
  const isFirst = index === 0;

  const prepareStep = useCallback(
    async (s: ProductTourStep) => {
      const mobile = isMobileViewport();
      const needsSidebar =
        Boolean(s.requiresMobileSidebar) || Boolean(s.requiresSidebarExpanded);

      if (needsSidebar) {
        // Always expand desktop rail; close mobile drawer on desktop so the
        // expanded rail is the mounted tree (mobileOpen replaces it entirely).
        chatControls?.expandSidebar();
        if (mobile) {
          chatControls?.openMobileSidebar();
        } else {
          chatControls?.closeMobileSidebar();
        }
      } else if (
        s.id === 'welcome' ||
        s.id.startsWith('nav-') ||
        s.id === 'composer' ||
        s.id === 'done'
      ) {
        chatControls?.closeMobileSidebar();
      }

      await waitFrames(2);
      if (s.target) {
        const deadline = Date.now() + 600;
        while (Date.now() < deadline && !findTarget(s.target)) {
          await new Promise((r) => setTimeout(r, 40));
        }
        // One more frame after the target mounts so layout is final.
        await waitFrames(1);
      }
    },
    [chatControls],
  );

  const reposition = useCallback(() => {
    const s = steps[index];
    if (!s) return;
    if (!s.target) {
      setHole(null);
      setCardStyle({
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        maxWidth: '22rem',
        width: 'calc(100vw - 2rem)',
      });
      return;
    }
    const el = findTarget(s.target);
    if (!el) {
      setHole(null);
      setCardStyle({
        position: 'fixed',
        bottom: '1.5rem',
        left: '50%',
        transform: 'translateX(-50%)',
        maxWidth: '22rem',
        width: 'calc(100vw - 2rem)',
      });
      return;
    }
    el.scrollIntoView({
      block: 'nearest',
      inline: 'nearest',
      behavior: 'instant' as ScrollBehavior,
    });
    const rect = measure(el);
    if (rect.width < 1 || rect.height < 1) {
      setHole(null);
      return;
    }
    setHole({
      top: Math.max(0, rect.top - PAD),
      left: Math.max(0, rect.left - PAD),
      width: rect.width + PAD * 2,
      height: rect.height + PAD * 2,
    });

    const cardH = cardRef.current?.offsetHeight ?? 180;
    const cardW = Math.min(352, window.innerWidth - 32);
    // Prefer placing the card to the right of left-rail targets (sidebar).
    const spaceRight = window.innerWidth - (rect.left + rect.width);
    const spaceBelow = window.innerHeight - (rect.top + rect.height + PAD);
    const placeRight = rect.left < 320 && spaceRight >= cardW + CARD_GAP;

    let top: number;
    let left: number;
    if (placeRight) {
      left = rect.left + rect.width + PAD + CARD_GAP;
      top = Math.max(16, Math.min(rect.top, window.innerHeight - cardH - 16));
      if (left + cardW > window.innerWidth - 16) {
        left = Math.max(16, window.innerWidth - cardW - 16);
      }
    } else {
      const placeBelow =
        spaceBelow >= cardH + CARD_GAP || rect.top < cardH + CARD_GAP;
      if (placeBelow) {
        top = rect.top + rect.height + PAD + CARD_GAP;
        if (top + cardH > window.innerHeight - 16) {
          top = Math.max(16, window.innerHeight - cardH - 16);
        }
      } else {
        top = rect.top - PAD - CARD_GAP - cardH;
        if (top < 16) top = 16;
      }
      left = rect.left + rect.width / 2 - cardW / 2;
      left = Math.max(16, Math.min(left, window.innerWidth - cardW - 16));
    }
    setCardStyle({
      position: 'fixed',
      top,
      left,
      width: cardW,
      maxWidth: 'calc(100vw - 2rem)',
      transform: 'none',
    });
  }, [index, steps]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const s = steps[index];
      if (!s) return;
      await prepareStep(s);
      if (!cancelled) reposition();
    })();
    return () => {
      cancelled = true;
    };
  }, [index, steps, prepareStep, reposition]);

  useLayoutEffect(() => {
    reposition();
  }, [reposition]);

  useEffect(() => {
    const onResize = () => reposition();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
    };
  }, [reposition]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (saving) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        onSkip();
        return;
      }
      if (e.key === 'ArrowRight' || e.key === 'Enter') {
        e.preventDefault();
        if (isLast) onComplete();
        else setIndex((i) => Math.min(total - 1, i + 1));
        return;
      }
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setIndex((i) => Math.max(0, i - 1));
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [saving, isLast, onSkip, onComplete, total]);

  // Focus trap: focus primary button when step changes
  useEffect(() => {
    const btn = cardRef.current?.querySelector<HTMLButtonElement>('[data-tour-primary]');
    btn?.focus();
  }, [index]);

  if (!step) return null;

  return (
    <div
      className="fixed inset-0 z-[60]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="product-tour-title"
      aria-describedby="product-tour-body"
    >
      {/* Dim: full veil when no target; spotlight hole via box-shadow otherwise */}
      {!hole ? (
        <div className="absolute inset-0 bg-black/45" aria-hidden />
      ) : (
        <div
          className="pointer-events-none absolute rounded-xl ring-2 ring-white/90"
          style={{
            top: hole.top,
            left: hole.left,
            width: hole.width,
            height: hole.height,
            boxShadow: '0 0 0 9999px rgba(0,0,0,0.45)',
            background: 'transparent',
          }}
          aria-hidden
        />
      )}

      <div
        ref={cardRef}
        style={cardStyle}
        className="z-[61] rounded-2xl border border-stone-200 bg-canvas-elevated p-4 shadow-soft"
      >
        <div className="mb-1 flex items-center justify-between gap-2">
          <p className="text-[11px] font-medium uppercase tracking-wide text-stone-400">
            {index + 1} / {total}
          </p>
          <button
            type="button"
            onClick={onSkip}
            disabled={saving}
            className="text-xs text-stone-500 underline-offset-2 hover:text-stone-800 hover:underline disabled:opacity-50"
          >
            Skip tour
          </button>
        </div>
        <h2
          id="product-tour-title"
          className="text-base font-semibold text-stone-900"
          aria-live="polite"
        >
          {step.title}
        </h2>
        <p id="product-tour-body" className="mt-1.5 text-sm leading-relaxed text-stone-600">
          {step.body}
        </p>
        {error && (
          <p className="mt-2 text-xs text-rose-600" role="alert">
            {error}
          </p>
        )}
        <div className="mt-4 flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={() => setIndex((i) => Math.max(0, i - 1))}
            disabled={isFirst || saving}
            className="rounded-xl px-3 py-1.5 text-xs font-medium text-stone-600 hover:bg-stone-100 disabled:opacity-40"
          >
            Back
          </button>
          <button
            type="button"
            data-tour-primary
            onClick={() => {
              if (isLast) onComplete();
              else setIndex((i) => Math.min(total - 1, i + 1));
            }}
            disabled={saving}
            className="rounded-xl bg-stone-900 px-3.5 py-1.5 text-xs font-medium text-stone-50 hover:bg-stone-800 disabled:opacity-50"
          >
            {saving ? 'Saving…' : isLast ? 'Start chatting' : isFirst ? 'Start' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
}
